import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Card,
    DatePicker,
    Input,
    Space,
    Pagination,
    message,
    Modal,
    Typography
} from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getParlamentDecisions, deleteParlamentDecision } from '../../services/parlamentDecisionService';

const { Text } = Typography;

const ParlamentDecisionList = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 14,
        total: 0
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState([
        dayjs('2021-10-14'),
        dayjs('2030-07-27')
    ]);

    const navigate = useNavigate();

    const fetchData = async (page = 1) => {
        try {
            setLoading(true);
            const params = {
                pageNum: page,
                pageSize: pagination.pageSize,
                searchTerm: searchTerm,
                sortBy: '',
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD')
            };

            const response = await getParlamentDecisions(params);
            if (response && response.items) {
                setData(response.items || []);
                setPagination({
                    ...pagination,
                    current: page,
                    total: response.total || 0
                });
            } else {
                setData([]);
                setPagination({
                    ...pagination,
                    current: 1,
                    total: 0
                });
            }
        } catch (error) {
            message.error('Meclis kararları yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const columns = [
        {
            title: 'Tarih',
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('DD.MM.YYYY')
        },
        {
            title: 'Karar No',
            dataIndex: 'decisionsNo',
            key: 'decisionsNo'
        },
        {
            title: 'Başlık',
            dataIndex: 'header',
            key: 'header'
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/parlament-decisions/edit/${record.guid}`)}
                    >
                        Düzenle
                    </Button>
                    <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    >
                        Sil
                    </Button>
                </Space>
            )
        }
    ];

    const handleSearch = (value) => {
        setSearchTerm(value);
        fetchData(1);
    };

    const handleDateChange = (dates) => {
        setDateRange(dates);
        fetchData(1);
    };

    const handleDelete = (record) => {
        setSelectedRecord(record);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedRecord?.guid) return;

        try {
            setTableLoading(true);
            const response = await deleteParlamentDecision(selectedRecord.guid);
            
            if (response.isSuccess) {
                message.success('Meclis kararı başarıyla silindi.');
                setShowDeleteModal(false);
                setSelectedRecord(null);
                window.location.reload();
            } else {
                message.error(response.message || 'Silme işlemi başarısız.');
            }
        } catch (error) {
            console.error('Silme hatası:', error);
            message.error('Meclis kararı silinirken bir hata oluştu.');
        } finally {
            setTableLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setSelectedRecord(null);
    };

    return (
        <>
        <Card title="Meclis Kararları">
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/parlament-decisions/add')}
                    >
                        Yeni Meclis Kararı Ekle
                    </Button>
                    <DatePicker.RangePicker
                        value={dateRange}
                        onChange={handleDateChange}
                    />
                    <Input.Search
                        placeholder="Ara..."
                        onSearch={handleSearch}
                        style={{ width: 300 }}
                    />
                </Space>
            </Space>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="guid"
                loading={loading || tableLoading}
                pagination={false}
            />

            <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Pagination
                    current={pagination.current}
                    total={pagination.total}
                    pageSize={pagination.pageSize}
                    onChange={(page) => fetchData(page)}
                    showSizeChanger={false}
                />
            </div>
        </Card>

        <Modal
            title="Meclis Kararı Sil"
            open={showDeleteModal}
            onOk={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            okText="Sil"
            cancelText="İptal"
            okButtonProps={{
                danger: true,
                loading: tableLoading
            }}
            maskClosable={false}
            keyboard={false}
            closable={false}
            destroyOnClose
        >
            {selectedRecord && (
                <div>
                    <p>Bu meclis kararını silmek istediğinizden emin misiniz?</p>
                    <p>
                        <Text strong>Başlık:</Text> {selectedRecord.header}<br />
                        <Text strong>Karar No:</Text> {selectedRecord.decisionsNo}
                    </p>
                </div>
            )}
        </Modal>
        </>
    );
};

export default ParlamentDecisionList;
